import { ResourceDetail, TraitId } from '@cibo/core'
import LockIcon from '@mui/icons-material/Lock'
import {
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
} from '@mui/material'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useDetailEditing } from '../../features'
import { useLandReportView } from './LandReportViewProvider'

const StyledTableCell = styled(TableCell)(() => ({
  whiteSpace: 'nowrap',
}))

interface DetailPanelColumn<T> {
  header: string
  cellContents: (yearData?: T) => ReactElement
}

interface DetailPanelTableProps<T> {
  columns: DetailPanelColumn<T>[]
  traitId: TraitId
}

export function DetailPanelTable<T extends ResourceDetail>({
  columns,
  traitId,
}: DetailPanelTableProps<T>): ReactElement {
  const { resourceId } = useParams()
  const { field, focusYear } = useLandReportView({ resourceId })
  const { t } = useTranslation('@cibo/landmanager/LandReport')
  const { setBulkEditModal } = useDetailEditing()
  const handleEditAll = () => {
    if (!!resourceId) {
      setBulkEditModal({
        resourceIds: [resourceId],
        traitId: traitId,
        requirements: field.data?.reportYears.map(year => ({ traitId, year, dataType: 'field' })),
      })
    }
  }
  if (!!field.data) {
    return (
      <Stack spacing={1} alignItems="flex-end" data-testid={`detailPanelTable-${traitId}`}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell>{t('year')}</StyledTableCell>
                <StyledTableCell aria-label={t('locked')}>{}</StyledTableCell>
                {columns.map((c: DetailPanelColumn<T>) => (
                  <StyledTableCell key={c.header}>{c.header}</StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {field.data.reportYears.map(year => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const yearData = (field.data!.traitDetails<T>(traitId) as T[]).find(
                  c => c.year === year
                )
                return (
                  <TableRow key={year} selected={year === focusYear}>
                    <StyledTableCell>{year}</StyledTableCell>
                    <StyledTableCell sx={{ alignItems: 'center' }}>
                      {yearData?.immutable && <LockIcon color="disabled" fontSize="small" />}
                    </StyledTableCell>
                    {columns.map((column: DetailPanelColumn<T>, index) => (
                      <StyledTableCell key={`${focusYear}.${traitId}.${index}`}>
                        {column.cellContents(yearData)}
                      </StyledTableCell>
                    ))}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Button
          variant="outlined"
          disabled={!field.data?.can(['edit_meta'])}
          onClick={handleEditAll}
          size="small"
        >
          {t('edit')}
        </Button>
      </Stack>
    )
  }
  return <></>
}
